import { ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, signal } from '@angular/core';
import { AcTranslationService } from '@core/services/translation/ac-translation.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { appSelectors } from '@store/selectors/app.selectors';
import { LoadingIds } from '@shared/constants/loading';
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent } from '@angular/material/dialog';
import { SpanishPrivacyPolicyComponent } from '@pages/privacy-policy/ui/spain/spanish-privacy-policy.component';
import { CheckboxComponent } from '@shared/forms/checkbox/checkbox.component';
import { ButtonComponent } from '@shared/ui-components/button/button.component';
import { LoadingPanelComponent } from '@shared/ui-components/loading-panel/loading-panel.component';
import { AcDialogService } from '@core/services/dialog/ac-dialog.service';
import { filter, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthUser } from '@core/domain/model/user/auth-user.model';
import { userActions } from '@store/actions/user.actions';
import { userSelectors } from '@store/selectors/user.selectors';
import { MatDivider } from '@angular/material/divider';
import { PortugueseSpanishPolicyComponent } from '@pages/privacy-policy/ui/portugal/portuguese-spanish-policy.component';

@Component({
  selector: 'ac-user-data-protection-dialog',
  standalone: true,
  imports: [
    NgOptimizedImage,
    TranslateModule,
    MatDialogContent,
    SpanishPrivacyPolicyComponent,
    NgTemplateOutlet,
    MatDialogActions,
    CheckboxComponent,
    ButtonComponent,
    ReactiveFormsModule,
    LoadingPanelComponent,
    MatDivider,
    PortugueseSpanishPolicyComponent,
  ],
  templateUrl: 'data-protection.dialog.html',
  styleUrl: 'data-protection.dialog.scss',
  host: {
    class: 'parsed-html',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataProtectionDialog {
  protected readonly _langSig = this._translation.langSig;
  protected readonly _acceptDataProtectionPolicyControl = new FormControl<boolean>(false, { nonNullable: true });
  protected readonly _showFullPolicy = signal(false);
  protected readonly _loadingSig = computed(() =>
    this._store.selectSignal(appSelectors.specificLoading)().includes(LoadingIds.PUT_DATA_PROTECTION)
  );
  protected readonly _countriesOrder = computed(() => {
    const country = this._user.country;
    const countriesOrder = [country];
    if (this._store.selectSignal(userSelectors.multipleCountries)()) {
      countriesOrder.push(country === 'ES' ? 'PT' : 'ES');
    }
    return countriesOrder;
  });
  constructor(
    @Inject(MAT_DIALOG_DATA) protected readonly _user: AuthUser,
    private _store: Store<StoreState>,
    private _translation: AcTranslationService,
    private _acDialog: AcDialogService,
    private _destroy: DestroyRef
  ) {}
  protected _save(): void {
    if (this._acceptDataProtectionPolicyControl.value) {
      this._store.dispatch(userActions.userDataProtectionResponse({ country: this._user.country, response: true }));
    } else {
      this._acDialog
        .confirmationDialog('PRIVACY.CONFIRMATION.TITLE', 'PRIVACY.CONFIRMATION.SUBTITLE')
        .pipe(
          filter((value) => !!value),
          take(1),
          takeUntilDestroyed(this._destroy)
        )
        .subscribe(() => this._store.dispatch(userActions.userDataProtectionResponse({ country: this._user.country, response: false })));
    }
  }
}
